import React, { useState, useEffect, useRef } from 'react';
import { View, Text, FlatList, SafeAreaView, ScrollView, TextStyle } from 'react-native';
import { useStore } from 'Store';
import { t } from 'Locale/index';
import { Button, Hr } from 'LocalGluons/index';

import { Device } from 'DeviceUtils';
import {
    ConsentsToSaveRequest,
    ConsentToSave
} from '@24i/st-api-connector/interface/auth/requests';
import { CachedImage } from 'LocalGluons/components/CachedImage';
// eslint-disable-next-line import/no-unresolved
import { ConsentDefinition } from 'types';
import LinearGradient from 'react-native-linear-gradient';
import { NativeConsent } from '@24i/st-api-connector';
import RedirectScene from '../../../runtime/Page/RedirectScene';
import { possibleApps } from '../../../runtime/Page/RedirectScene/getVoyoStoreParameters';
import portraitStyles, { landscapeStyles } from './CanalplusConsentDialog.jss';
import CanalPlusConsentItem from './CanalplusConsentItem/CanalplusConsentItem';
import { renderLinks } from '../ParseUrlString/parseURLString';
// eslint-disable-next-line @typescript-eslint/no-var-requires

interface CanalplusConsentDialogProps {
    title: string;
    titleDescription: string;
    isLandscape: boolean;
    consentDefinitions: ConsentDefinition[];
    onAccept: () => void;
    onClose: () => void;
    setShowConsentsAgain: (state: any) => void;
    navigation?: any;
    route?: any;
}

const CanalplusConsentDialog = ({
    navigation,
    route,
    ...props
}: CanalplusConsentDialogProps): JSX.Element => {
    const { appManager } = useStore();
    const [selectedNames, setSelectedNames] = useState<Array<string>>([]);
    const selectedIdsRef = useRef(selectedNames);
    selectedIdsRef.current = selectedNames;
    const { title, titleDescription, isLandscape, consentDefinitions,
        onAccept, onClose, setShowConsentsAgain
    } = route ? route.params : props;
    const [isButtonEnabled, setButtonEnabled] = useState<boolean>(false);
    const {
        backstageConnector: { auth, vod }
    } = appManager.getApi();

    const onSelectedPress = (pressedName: string) => {
        setSelectedNames((currentSelectedNames: Array<string>) =>
            currentSelectedNames.includes(pressedName)
                ? [...currentSelectedNames.filter(name => name !== pressedName)]
                : [...currentSelectedNames, pressedName]);
    };

    // TODO: I used logic from NotificagionDialog refactore to get rid of isLandscape with this on both places
    const styles = isLandscape ? landscapeStyles : portraitStyles;
    const buttonStyle = { button: styles.button };
    const buttonTabletStyle = {
        button: landscapeStyles.button,
        text: landscapeStyles.buttonText,
        textContainer: landscapeStyles.buttonContainerText,
    };

    const onCancelPress = () => {
        // setShowConsentsAgain(true);
        if (Device.isWeb) {
            onClose();
        } else {
            navigation.pop();
        }
    };
    const onContinuePress = async () => {
        const consentsToSaveRequest: ConsentsToSaveRequest = {
            consents: consentDefinitions.map((item: NativeConsent) => {
                const consentToSave: ConsentToSave = {
                    name: item.name,
                    value: selectedNames.includes(item.name)
                };
                return consentToSave;
            })
        };

        try {
            await auth.saveConsents('CANALPLUS', consentsToSaveRequest);
            setShowConsentsAgain(false);
            onAccept();
            if (Device.isWeb) {
                onClose();
            } else {
                navigation.pop();
            }
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        const isClickAllowed = consentDefinitions.every((item: NativeConsent) => {
            if (item.mandatory && !selectedNames.includes(item.name)) {
                return false;
            }
            return true;
        });
        setButtonEnabled(isClickAllowed);
    }, [selectedNames, setButtonEnabled, consentDefinitions]);

    return (
        <SafeAreaView style={styles.safeAreaContainer}>
            {Device.isPhone && (
                <View style={styles.scrollViewContainer}>
                    <ScrollView contentContainerStyle={styles.contentScrollview}>
                        <View style={styles.windowMobile}>
                            <Text style={styles.subTitle}>{title}</Text>
                            <Text style={styles.greyText}>{titleDescription}</Text>
                            <Hr />
                            <FlatList
                                data={consentDefinitions}
                                keyExtractor={(item) => item.name}
                                extraData={selectedNames}
                                scrollEnabled={false}
                                renderItem={({ item, index }) => (
                                    <CanalPlusConsentItem
                                        last={consentDefinitions.length - 1 === index}
                                        item={item}
                                        isLandscape={false}
                                        isSelected={selectedNames.includes(item.name)}
                                        onSelectedPress={onSelectedPress}
                                    />
                                )}
                            />
                        </View>
                    </ScrollView>
                    <View style={styles.buttonContainer}>
                        <Button
                            style={buttonStyle}
                            title={t('PARTNER.CANALPLUS.CANCEL_BTN')}
                            isSecondary
                            onPress={onCancelPress}
                        />
                        <Button
                            style={buttonStyle}
                            title={t('PARTNER.CANALPLUS.CONTINUE_BTN')}
                            onPress={onContinuePress}
                            isDisabled={!isButtonEnabled}
                            isSecondary={!isButtonEnabled}
                        />
                        <View style={styles.gradientContainer} pointerEvents="none">
                            <LinearGradient
                                style={styles.gradient}
                                colors={['#00000000', '#000000FF']}
                            />
                        </View>
                    </View>
                </View>
            )}

            {Device.isTabletOrWeb && (
                <ScrollView
                    style={styles.scrollViewContainer}
                    contentContainerStyle={Device.isWeb
                        ? landscapeStyles.containerWeb
                        : styles.contentScrollview}
                >
                    <View style={Device.isWeb
                        ? landscapeStyles.overlayWeb
                        : landscapeStyles.windowTablet}>
                        <View style={landscapeStyles.headerTextContainer}>
                            <Text style={landscapeStyles.title}>{title}</Text>
                            <Text style={landscapeStyles.subTitle}>{titleDescription}</Text>
                        </View>
                        <Hr />
                        {consentDefinitions.map((item, index) => (
                            <CanalPlusConsentItem
                                last={consentDefinitions.length - 1 === index}
                                item={item}
                                key={item.name}
                                isLandscape={false}
                                isSelected={selectedNames.includes(item.name)}
                                onSelectedPress={onSelectedPress}
                                style={{
                                    text: styles.item as TextStyle,
                                    container: styles.itemContainer
                                }}
                            />
                        ))}
                        <View style={landscapeStyles.buttonContainer}>
                            <Button
                                // style={buttonTabletStyle}
                                title={t('PARTNER.CANALPLUS.CANCEL_BTN')}
                                isSecondary
                                onPress={onCancelPress}
                            />
                            <Button
                                // style={buttonTabletStyle}
                                title={t('PARTNER.CANALPLUS.CONSENTS.I_AGREE_MOBILES')}
                                onPress={onContinuePress}
                                isDisabled={!isButtonEnabled}
                                isSecondary={!isButtonEnabled}
                            />
                        </View>
                    </View>
                </ScrollView>
            )}
        </SafeAreaView>
    );
};

export default CanalplusConsentDialog;
